import { map } from 'lodash';
import { Icon } from '../../generic-icon/Icon';
import {
  Attribute,
  AttributeLabel,
  AttributesContainer,
  Container,
  DestinationLabel,
  HotelNameLabel,
  ImageCrossfadeContainer,
  PriceContainer,
  RatingContainer,
  tripAdvisorRating
} from '../styles';
import { DOW_DAY_MONTH_FORMAT } from '@model/common/dates';
import { AspectRatios } from '@model/common/image';
import { Result } from '@model/iceberg/deal-finder/deal-finder';
import { Theme } from '@theme/base';
import { withTheme } from '@emotion/react';
import moment from 'moment';
import React, { forwardRef, useState } from 'react';
import { StarRating } from '../../star-rating/StarRating';
import { getCrossfadeImages } from '@util/product';
import { ImageCrossfade } from '../../image-crossfade/ImageCrossfade';

export interface ProductTileComponentDynamicProps {
  testFlag?: boolean;
  testId?: string;
  productPackage: Result;
  className?: string;
  onClick?: () => void;
  renderPrice: JSX.Element | null;
  boardBasis: string;
}

export interface ProductTileComponentFixedProps {
  theme?: Theme;
}

type ProductTileComponentProps = ProductTileComponentDynamicProps & ProductTileComponentFixedProps;

/* ************************** *
 *    ProductTileComponent    *
 * ************************** */

export const ProductTileComponent = forwardRef((props: ProductTileComponentProps, ref: any) => {
  const {
    theme,
    testId,
    renderPrice,
    className,
    productPackage: {
      summary: {
        nights,
        startDate,
        place: { region, hotel, resort },
        hotelRating
      }
    },
    productPackage: { summary },
    onClick,
    boardBasis
  } = props;
  const productImages = map(getCrossfadeImages(summary.featuredImage, summary.gallery), 'url');

  /* *** STATE *** */
  const [isHover, setIsHover] = useState(false);

  /* *** RENDERERS *** */
  return (
    <Container
      ref={ref}
      data-testid={testId || 'mh-product-package'}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      className={className}
      onClick={onClick}
      {...props}
    >
      <ImageCrossfadeContainer>
        <ImageCrossfade images={productImages} triggerPlay={isHover} aspectRatio={AspectRatios['16:9']} />
      </ImageCrossfadeContainer>
      <DestinationLabel className={'global-hover'}>
        {resort.display}, {region.display}
      </DestinationLabel>
      <HotelNameLabel className={'global-hover'}>{hotel!.display}</HotelNameLabel>
      {!!renderPrice && (
        <AttributesContainer>
          <Attribute>
            <Icon
              className={'global-hover'}
              name={'mhi-calendar'}
              css={{ fontSize: '1.2rem', color: theme!.custom.colors.group4.base }}
            />
            <AttributeLabel className={'global-hover'}>{moment(startDate).format(DOW_DAY_MONTH_FORMAT)}</AttributeLabel>
          </Attribute>
          {nights && (
            <Attribute>
              <Icon
                className={'global-hover'}
                name={'mhi-nights'}
                css={{ fontSize: '1.4rem', color: theme!.custom.colors.group4.base }}
              />
              <AttributeLabel className={'global-hover'}>{nights} nights</AttributeLabel>
            </Attribute>
          )}
          <Attribute>
            <Icon
              className={'global-hover'}
              name={'mhi-meal'}
              css={{ fontSize: '1.4rem', color: theme!.custom.colors.group4.base }}
            />
            <AttributeLabel className={'global-hover'}>{boardBasis}</AttributeLabel>
          </Attribute>
        </AttributesContainer>
      )}
      <PriceContainer>{renderPrice}</PriceContainer>
      <RatingContainer>
        <StarRating rating={hotelRating} />
        {summary.tripAdvisorRating && (
          <img
            className={tripAdvisorRating}
            src={summary.tripAdvisorRating}
            alt={'TripAdvisor rating'}
            height={20}
            width={120}
          />
        )}
      </RatingContainer>
    </Container>
  );
});

export const BaseProductTile = withTheme(ProductTileComponent);
